'use strict';

const ops = require('./ops');
const BN = require('bn.js');
const {
  addHexPrefix,
  coerceToBN,
  leftZeroPadToByteLength
} = require('./util');

const isLabelQuick = (s, labels) => labels[s];
const isLabel = (s) => isNaN(s) && !ops[s] && !Array.isArray(s);

const pushBytes = (r, bytes) => {
  if (!r.currentLabel) r.initial[r.initial.length - 1] += bytes;
  else {
    const partial = r.labels[r.currentLabel];
    partial[partial.length - 1] += bytes;
  }
  return r;
};

const pushLabel = (r, label) => {
  if (!r.currentLabel) {
    const { initial } = r;
    initial.push(label);
    initial.push('');
  } else {
    const partial = r.labels[r.currentLabel];
    partial.push(label);
    partial.push('');
  }
  return r;
};

const encodePush = (v, length) => {
  const baseOffset = Number(addHexPrefix(ops.push));
  const opcodeValue = baseOffset + length - 1;
  return opcodeValue.toString(16) + leftZeroPadToByteLength(v.toString(16), length);
};

const firstPass = (ast, progress = { labels: {}, segmentOrder: [], initial: [''], pass: new WeakMap() }) => ast.reduce((r, v, i, ary) => {
  r = r || progress;
  if (r.pass.get(v)) return r;
  if (Array.isArray(v)) return firstPass(v, r);
  if (isLabel(v)) {
    if (!i && Array.isArray(ary[i + 1])) {
      r.labels[v] = [ ops.jumpdest ];
      r.segmentOrder.push(v);
      r.currentLabel = v;
      r.pass.set(ary[i + 1], true);
      return firstPass(ary[i + 1], r);
    }
    return pushLabel(r, v);
  }
  if (!isNaN(v)) {
    const bn = coerceToBN(v);
    const length = bn.byteLength() || 1;
    if (length > 32) throw Error('constant integer overflow: ' + v);
    return pushBytes(r, encodePush(bn, length));
  }
  const op = ops[v];
  if (!op) throw Error('opcode not found: ' + v);
  return pushBytes(r, op);
}, null);

const initialSegmentSymbol = Symbol('@@initial');

const mergeInitial = (meta) => {
  const {
    labels,
    initial,
    segmentOrder
  } = meta;
  labels[initialSegmentSymbol] = initial;
  segmentOrder.unshift(initialSegmentSymbol);
};

const findOptimalJumpdestSize = ({
  labels,
  segmentOrder
}) => {
  let totalMin = 0, dynamicSlots = 0;
  segmentOrder.forEach((label) => {
    labels[label].forEach((partial) => {
      if (isLabelQuick(partial, labels)) dynamicSlots++;
      else totalMin += partial.length / 2;
    });
  });
  return totalMin + dynamicSlots*2 <= 256 ? 2 : 3;
};

const annotateJumpdestOffsets = ({
  segmentOrder,
  labels
}, width) => {
  let passed = 0;
  segmentOrder.forEach((v) => {
    const label = labels[v];
    label.jumpdest = passed;
    label.forEach((partial) => (passed += (isLabelQuick(partial, labels) ? width : partial.length / 2)));
  });
};

const encodeJumpdestPushes = ({
  segmentOrder,
  labels
}, width) => {
  segmentOrder.forEach((v) => {
    labels[v].forEach((partial, i, ary) => {
      if (isLabelQuick(partial, labels)) ary[i] = encodePush(labels[partial].jumpdest, width - 1);
    });
  });
};

const assemble = (ast) => {
  const meta = firstPass(ast);
  mergeInitial(meta);
  const width = findOptimalJumpdestSize(meta);
  annotateJumpdestOffsets(meta, width);
  encodeJumpdestPushes(meta, width);
  const {
    labels,
    segmentOrder
  } = meta;
  return addHexPrefix(segmentOrder.map((v) => labels[v].join('')).join(''));
};

module.exports = assemble;
